import { useEffect, useState } from 'react';

export type KeyboardType = 'Number' | 'Alphabetic';

type UseScreenKeyboardState = {
  focusedInput: HTMLInputElement | null;
  setFocusedInput: (input: HTMLInputElement | null) => void;
  handleSetFocus: (focusedInput: HTMLInputElement, keyboardType?: KeyboardType) => void;
  showKeyboard: boolean;
  handleKeyPress: (key: string) => void;
  setKeyboardType: (key: KeyboardType) => void;
  keyboardType: KeyboardType;
};

type UseScreenKeyboardProps = {
  defaultKeyboardType?: KeyboardType;
  onChange: (value: React.ChangeEvent<HTMLInputElement>) => void;
};

export const useScreenKeyboard = (props: UseScreenKeyboardProps): UseScreenKeyboardState => {
  const { defaultKeyboardType = 'Alphabetic', onChange } = props;
  const [keyboardType, setKeyboardType] = useState<KeyboardType>(defaultKeyboardType);
  const [focusedInput, setFocusedInput] = useState<HTMLInputElement | null>(null);

  // Effect to temporarily change the input type to 'text' when it receives focus because cursor manipulation is not supported on email input
  useEffect(() => {
    if (focusedInput && focusedInput.tagName.toLowerCase() === 'input') {
      const previousType = focusedInput.type;
      focusedInput.type = 'text'; // Temporarily change the type to allow cursor manipulation
      return () => {
        focusedInput.type = previousType;
      };
    }
    return;
  }, [focusedInput]);

  const handleSetFocus: UseScreenKeyboardState['handleSetFocus'] = (input, keyboardType) => {
    if (!input) return;
    setFocusedInput(input);
    if (keyboardType) setKeyboardType(keyboardType);
  };

  const updateInputValue = (input: HTMLInputElement, value: string, cursorPosition: number) => {
    onChange({
      target: {
        ...input,
        name: input.name,
        value,
      },
    } as React.ChangeEvent<HTMLInputElement>);
    input.setSelectionRange(cursorPosition, cursorPosition); // update the cursor position
    const isCursorAtEnd = cursorPosition === value.length;
    // Adjust scroll position only if the cursor is at the end
    if (isCursorAtEnd) {
      // Scroll horizontally for single-line inputs
      if (input.scrollWidth > input.clientWidth) {
        input.scrollLeft = input.scrollWidth;
      }
      // Scroll vertically for multi-line inputs
      if (input.scrollHeight > input.clientHeight) {
        input.scrollTop = input.scrollHeight;
      }
    }
  };

  const handleKeyPress = (key: string) => {
    if (focusedInput) {
      const start = focusedInput.selectionStart || 0;
      const end = focusedInput.selectionEnd || 0;
      const value = focusedInput.value ?? '';

      switch (key) {
        case 'AlphabeticPad': {
          setKeyboardType('Alphabetic');
          break;
        }
        case 'NumberPad': {
          setKeyboardType('Number');
          break;
        }
        case '{enter}': {
          setFocusedInput(null);
          focusedInput.blur();
          break;
        }
        case '{bksp}': {
          // Remove the character before the cursor
          if (start > 0) {
            const updatedValue = value.substring(0, start - 1) + value.substring(end);
            updateInputValue(focusedInput, updatedValue, start - 1);
          }
          break;
        }
        case 'Close': {
          setFocusedInput(null);
          focusedInput.blur();
          break;
        }
        case '{space}': {
          // Insert a space at the cursor position
          const updatedValue = value.substring(0, start) + ' ' + value.substring(end);
          updateInputValue(focusedInput, updatedValue, start + 1);
          break;
        }
        default: {
          // Insert the key at the cursor position
          const updatedValue = value.substring(0, start) + key + value.substring(end);
          updateInputValue(focusedInput, updatedValue, start + 1);
          break;
        }
      }
    }
  };

  return {
    setFocusedInput,
    setKeyboardType,
    focusedInput,
    keyboardType,
    handleSetFocus,
    showKeyboard: Boolean(focusedInput),
    handleKeyPress,
  };
};
